import { parseHoursString } from "../../components/features/Business/BusinessTabs/tabs/more/sections/converters/hoursStringConvertor";
import { BusinessHours, DayKey } from "../../interfaces/BusinessObject";

const DAY_KEYS: DayKey[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

// Stored hours strings carry no timezone — the app is India-only, so "now" is
// always read in IST, matching the existing hardcoded-IST pattern in
// OrderCard.tsx's formatWhen.
function istParts(now: Date): { dayKey: DayKey; minutesSinceMidnight: number } {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    weekday: "short",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  });
  const parts = fmt.formatToParts(now).reduce<Record<string, string>>((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  const weekdayIndex = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(parts.weekday);
  const dayKey = DAY_KEYS[weekdayIndex];

  const hour = parseInt(parts.hour, 10) % 24;
  const minute = parseInt(parts.minute, 10);
  return { dayKey, minutesSinceMidnight: hour * 60 + minute };
}

function to12HourMinutes(time: string): number | null {
  const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === "AM") {
    if (hour === 12) hour = 0;
  } else if (hour !== 12) {
    hour += 12;
  }
  return hour * 60 + minute;
}

/**
 * Whether the business is open right now, based on its configured hours
 * (moreDetails.hours). Returns false for missing/unparseable/closed entries —
 * callers should treat "no hours configured at all" as a separate case
 * (don't render a badge) rather than relying on this to distinguish it.
 */
export function isBusinessOpenNow(hours: BusinessHours | undefined, now: Date = new Date()): boolean {
  if (!hours) return false;

  const { dayKey, minutesSinceMidnight } = istParts(now);
  const raw = hours[dayKey];
  // A day the owner never touched in the editor is absent from the stored
  // object entirely (not "Closed") — parseHoursString would otherwise fall
  // back to its 9-9 default state and falsely report the business as open.
  if (typeof raw !== "string" || raw.trim().length === 0) return false;

  const { open, close, closed } = parseHoursString(raw);
  if (closed) return false;

  const openMin = to12HourMinutes(open);
  const closeMin = to12HourMinutes(close);
  if (openMin == null || closeMin == null) return false;

  if (closeMin > openMin) {
    return minutesSinceMidnight >= openMin && minutesSinceMidnight < closeMin;
  }
  // Overnight span (e.g. 9:00 PM – 2:00 AM).
  return minutesSinceMidnight >= openMin || minutesSinceMidnight < closeMin;
}

/** True when at least one day has a non-empty hours string configured. */
export function hasAnyHoursConfigured(hours: BusinessHours | undefined): boolean {
  if (!hours) return false;
  return DAY_KEYS.some((k) => typeof hours[k] === "string" && hours[k]!.trim().length > 0);
}
